/**
 * Sync / Data services
 * Handles all product, pricing rules, loyalty, gift card, UOM, and sale-type sync operations.
 */
import axios from "axios";
import { safeTrace, summarizeGiftCardTypes } from "../lib/printer/giftCardPrintType";
import dayjs from "dayjs";
import db from "../database";
import useSessionStore from "../stores/useSessionStore";
import useAuthStore from "../stores/useAuthStore";
import useTransactionStore from "../stores/useTransactionStore";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";
import { trimProductsBatch, trimProductData } from "../utils/trimProductData";
import useConfigStore from "../stores/useConfigStore";
import { buildUomQuery, buildPosSaleTypesQuery, buildProductsQuery, buildPricingRulesQuery, buildPricingPolicyQuery, mapProductToDb, mapPricingPolicyToDb } from "./syncQueries";
import { API_TIMEOUTS } from "./api/client";
import { trace } from "../utils/debugTrace";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";
// Without this, a request that never gets a network-level rejection (real
// offline connection, not a fast DNS/connection-refused error) hangs forever
// instead of failing — silently stalling the whole sync chain this instance
// drives (syncPosSaleTypes/syncProductsUom/syncPricingRules/syncPricingPolicies/
// syncProducts run sequentially via await in Sync/index.js's processSync).
// Matches the same default already used by the newer api/client.js instances.
retailInstance.defaults.timeout = API_TIMEOUTS.default;
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const syncAllProductCategories = (tillId, syncId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  return new Promise(async (posSaleTypeSyncSuccess, posSaleTypeSyncFailure) => {
    try {
      const paramsInput = {
        query: `query{
            getProductCategory(tillId:"${tillId}", lastSyncTime: ${lastUpdatedTime ? `"${lastUpdatedTime}"` : null}, timeZone:"${timeZone}"){
                mProductCategoryId
                name
                value
                description
                imageurl
                parentCategory{
                    mProductCategoryId
                    name
                    value
                    description
                    imageurl
                    parentCategory{
                        mProductCategoryId
                        name
                        value
                        description
                        imageurl
                        parentCategory{
                            mProductCategoryId
                            name
                            value
                            description
                            imageurl
                            parentCategory{
                                mProductCategoryId
                                name
                                value
                                description
                                imageurl
                            }
                        }
                    }
                }
            }
          }`,
      };

      const response = await retailInstance({
        data: paramsInput,
      });

      if (response.status === 200) {
        const { getProductCategory } = response.data.data;

        if (getProductCategory && getProductCategory.length > 0) {

          const isArray = [];
          const finalArray = [];
          const seenNames = new Set();

          function getParentCategoryValue(obj) {
            if (!obj.parentCategory) {
              const id = obj.mProductCategoryId;
              if (!seenNames.has(id)) {
                isArray.push(obj);
                seenNames.add(id);
              }
              return id;
            }
            obj.id = obj.parentCategory.mProductCategoryId;
            finalArray.push({
              mProductCategoryId: obj.mProductCategoryId,
              name: obj.name,
              value: obj.value,
              description: obj.description,
              imageurl: obj.imageurl,
              id: obj.id,
              syncId: syncId,
            });

            return getParentCategoryValue(obj.parentCategory);
          }

          for (const obj of getProductCategory) {
            getParentCategoryValue(obj);
          }

          const seenId = new Set();
          isArray.forEach((item) => {
            const printArray = finalArray.reduce((result, current) => {
              const category = current.id;
              if (!result[category]) {
                result[category] = [];
              }
              const id = current.mProductCategoryId;
              if (!seenId.has(id)) {
                seenId.add(id);
                result[category].push(current);
              }
              return result;
            }, {});
            item.printArray = printArray;
          });

          const lastId = await db.AllProductCategories.bulkPut(isArray);
          posSaleTypeSyncSuccess(lastId);
        } else {
          posSaleTypeSyncSuccess();
        }
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error: ${error}`);
      posSaleTypeSyncFailure(error);
    }
  });
};

const syncProductCategories = (tillData, tillId, syncId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  localStorage.setItem("sync1", true);
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  return new Promise(async (productCategorySyncSuccess, productCategorySyncFailure) => {
    try {
      const paramsInput = {
        query: `query {
              getProductCategory(tillId:"${tillId}", lastSyncTime: ${lastUpdatedTime ? `"${lastUpdatedTime}"` : null}, timeZone:"${timeZone}") {
              mProductCategoryId
              value
              name
              description
              imageurl
              }
            }`,
      };

      const response = await retailInstance({
        data: paramsInput,
      });

      const { getProductCategory: productCategory } = response.data.data;

      if (response.status === 200 && productCategory?.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime) {
          let recordsData = 0;
          const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");

          await Promise.all(
            productCategory.map(async (item) => {
              await db.productCategories
                .where("mProductCategoryId")
                .equals(item.mProductCategoryId)
                .toArray()
                .then(async (response) => {
                  if (response.length > 0) {
                    item.syncId = syncId;
                    await db.productCategories.update(item.mProductCategoryId, item);
                    recordsData = recordsData + 1;
                  } else {
                    item.syncId = syncId;
                    await db.productCategories.add(item);
                    recordsData = recordsData + 1;
                  }
                });
            })
          );
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, categories: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  categories: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.categories },
                };
              });
          }
          await syncProductBrand(syncId, tillId);
        } else {
          productCategory.forEach((item) => {
            item.syncId = syncId;
          });
          const lastId = await db.productCategories.bulkPut(productCategory);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, categories: { syncStartTime: syncStartTime, syncEndTime, records: productCategory.length } };
              event.records = (event.records ? event.records : 0) + productCategory.length;
            });
          productCategorySyncSuccess(lastId);
        }
        await syncProductBrand(syncId, tillId);
        productCategorySyncSuccess();
      } else {
        productCategorySyncSuccess();
        await syncProductBrand(syncId, tillId);
      }
    } catch (error) {
      productCategorySyncFailure(error);
    }
  });
};

const syncProductBrand = async (syncId, tillId) => {
  localStorage.setItem("sync1", true);
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  const userData = useAuthStore.getState().userData;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  if (!userData || !userData.cs_client_id) {
    console.error("User data or client ID is missing.");
    return Promise.reject("User data or client ID is missing.");
  }

  const paramsInput = {
    query: `query {
        getBrand(clientId: "${userData.cs_client_id}", lastSyncTime: ${
          lastUpdatedTime ? `"${lastUpdatedTime}"` : null
        },uniqueId: "${syncId}", tillId:"${tillId}", storeOpsTillId: null, timeZone:"${timeZone}") {
          brandId
          name
          value
        }
      }`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await retailInstance({
        data: paramsInput,
      });

      const { getBrand } = response.data.data;
      if (response.status === 200 && getBrand && getBrand.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            getBrand.map(async (item) => {
              const existingBrand = await db.productBrands.where("brandId").equals(item.brandId).first();
              if (existingBrand) {
                recordsData = recordsData + 1;
                item.syncId = syncId;
                await db.productBrands.update(item.brandId, item);
              } else {
                try {
                  item.syncId = syncId;
                  recordsData = recordsData + 1;
                  await db.productBrands.add(item);
                } catch (error) {
                  console.error(`Failed to add new brand with ID ${item.brandId}: `, error);
                }
              }
            })
          );
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, brands: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  brands: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.brands },
                };
              });
          }
        } else {
          getBrand.forEach((item) => {
            item.syncId = syncId;
          });
          await db.productBrands.bulkAdd(getBrand);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, brands: { syncStartTime: syncStartTime, syncEndTime, records: getBrand.length } };
              event.records = (event.records ? event.records : 0) + getBrand.length;
            });
        }
        resolve();
      } else {
        resolve();
      }
    } catch (error) {
      console.error("Product Brand Sync Failed: ", error);
      reject(error);
    }
  });
};

const syncPosSaleTypes = async (syncId, tillId, tillData) => {
  const lastUpdatedTime = null;
  await db.posSaletypes.clear();
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  return new Promise(async (posSaleTypeSyncSuccess, posSaleTypeSyncFailure) => {
    try {
      const paramsInput = buildPosSaleTypesQuery(tillData.tillAccess.csBunit.cwrPcatalogueId, { lastSyncTime: lastUpdatedTime, tillId, uniqueId: syncId, timeZone });

      const response = await retailInstance({
        data: paramsInput,
      });

      const { getPosSaletype } = response.data.data;

      if (response.status === 200 && getPosSaletype?.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");

        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            getPosSaletype.map(async (item) => {
              item.customer.sCustomer = { sCustomerID: item.customer.sCustomerId };
              item.syncId = syncId;
              await db.posSaletypes
                .where("cwrSaletypeId")
                .equals(item.cwrSaletypeId)
                .toArray()
                .then(async (response) => {
                  if (response.length > 0) {
                    recordsData = recordsData + 1;
                    await db.posSaletypes.update(item.cwrSaletypeId, item);
                  } else {
                    recordsData = recordsData + 1;
                    await db.posSaletypes.add(item);
                  }
                });
            })
          );

          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, posSaletypes: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  posSaletypes: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.posSaletypes },
                };
              });
          }
        } else {
          getPosSaletype.forEach((item) => {
            item.customer.sCustomer = { sCustomerID: item.customer.sCustomerId };
            item.syncId = syncId;
          });
          const lastId = await db.posSaletypes.bulkPut(getPosSaletype);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, posSaletypes: { syncStartTime: syncStartTime, syncEndTime, records: getPosSaletype.length } };
              event.records = (event.records ? event.records : 0) + getPosSaletype.length;
            });
          posSaleTypeSyncSuccess(lastId);
        }
      } else {
        posSaleTypeSyncSuccess();
      }
    } catch (error) {
      posSaleTypeSyncFailure(error);
    }
  });
};

const syncProductsUom = async (syncId, tillId) => {
  const lastUpdatedTime = null;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  return new Promise(async (uomSyncSuccess, uomSyncFailure) => {
    try {
      const paramsInput = buildUomQuery({ lastSyncTime: lastUpdatedTime, tillId, uniqueId: syncId, timeZone });

      const response = await retailInstance({
        data: paramsInput,
      });

      const { getUom } = response.data.data;

      if (response.status === 200 && getUom.length > 0) {
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            getUom.map(async (item) => {
              item.syncId = syncId;
              const existingUom = await db.productUom.where("csUomId").equals(item.csUomId).toArray();
              if (existingUom.length > 0) {
                recordsData = recordsData + 1;
                await db.productUom.update(item.csUomId, item);
              } else {
                recordsData = recordsData + 1;
                await db.productUom.add(item);
              }
            })
          );
          let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, UOM: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = { ...event.type, UOM: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.UOM } };
              });
          }
        } else {
          await db.productUom.clear();
          getUom.forEach((uom) => {
            uom.syncId = syncId;
          });
          const lastId = await db.productUom.bulkPut(getUom);
          let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, UOM: { syncStartTime: syncStartTime, syncEndTime, records: getUom.length } };
              event.records = (event.records ? event.records : 0) + getUom.length;
            });
          uomSyncSuccess(lastId);
        }
        uomSyncSuccess();
      } else {
        uomSyncSuccess();
      }
    } catch (error) {
      uomSyncFailure(error);
    }
  });
};

const syncProducts = async (tillData, syncId, tillId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  let recordsCount = 0;
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  // [BUG-1826] Per-page timing to confirm where full-sync time actually goes
  // after restoring the 5000-per-page limit — see comment on `limit` below.
  const productsSyncStart = performance.now();
  let pageCount = 0;
  trace("ProductsSync", "syncProducts start", { isFullSync: lastUpdatedTime === null });
  try {
    // Pre-load all UOMs into a Map for O(1) lookup — eliminates N+1 IPC (5000 serial calls per batch → 1 call total)
    const allUoms = await db.productUom.toArray();
    const uomMap = new Map();
    for (const uom of allUoms) {
      if (!uomMap.has(uom.csUomId)) uomMap.set(uom.csUomId, []);
      uomMap.get(uom.csUomId).push(uom);
    }
    // [BUG-1566-DIAG] uomMap built from db.productUom — if this is empty/small on an
    // incremental sync, every product below falls through to uomData:[] regardless
    // of whether the server actually has UOM data for it.
    console.debug("[BUG-1566-DIAG] syncProducts: uomMap built", {
      lastUpdatedTime,
      productUomRowCount: allUoms.length,
      uniqueCsUomIds: uomMap.size,
    });

    // Batches fetched from the server are paged at this size. A 5000-per-page
    // batch previously produced a single uninterrupted mapping-loop block long
    // enough (30-60s) to trip the UI watchdog (15s freeze threshold) and force a
    // reload mid-sync (BUG-1594). That was fixed by yielding every YIELD_CHUNK
    // items inside the mapping loop below, not by the page size.
    // BUG-1826 confirmed via side-by-side ProductsSync traces on the same 6591-row
    // catalog: limit=500 took 341.8s for the products stage (383.1s full sync,
    // 15 requests) vs limit=5000's 48.0s (89.3s full sync, 3 requests) — a 7.1x
    // regression, not just the ~10x-requests you'd expect from page count alone.
    // getProducts1's fetchMs scaled almost linearly with page offset (~4ms/record
    // of offset, same at every page), so a small page size pays that per-page
    // scan penalty far more times — do not shrink this again without a
    // cursor/keyset fix on the getProducts1 resolver first.
    let from = 0;
    let limit = 5000;
    let productsSynced = false;
    while (!productsSynced) {
      const syncParams = { lastSyncTime: lastUpdatedTime, tillId, uniqueId: syncId, timeZone };
      const paramsInput = buildProductsQuery(tillData.tillAccess.csBunit.csBunitId, from, limit, syncParams);
      const pageFrom = from;
      from += limit;
      const pageStart = performance.now();
      const response = await retailInstance({
        data: paramsInput,
      });
      const fetchMs = Math.round(performance.now() - pageStart);

      const { getProducts1 } = response.data.data;

      const products = getProducts1;
      const productsLength = products.length;
      pageCount += 1;
      if (productsLength > 0) {
        recordsCount = recordsCount + productsLength;
        const productsData = [];
        const brands = new Set();
        const categories = new Set();
        // [BUG-1566-DIAG] tracks which products resolve to uomData:[] during this sync batch
        let uomMissCount = 0;
        const uomMissSamples = [];
        // Yield to the event loop every YIELD_CHUNK items so long full-syncs never
        // block the main thread long enough to trip the UI watchdog (see comment above).
        const YIELD_CHUNK = 200;
        const mapWriteStart = performance.now();
        for (let i = 0; i < productsLength; i += 1) {
          const csUomId = products[i].mProduct.csUomId;
          const uomData = uomMap.get(csUomId) || [];
          if (uomData.length === 0) {
            uomMissCount += 1;
            if (uomMissSamples.length < 10) {
              uomMissSamples.push({ value: products[i].mProduct.value, csUomId });
            }
          }
          const productDataObj = mapProductToDb(products[i], uomData, { syncId });
          productsData.push(productDataObj);
          if (i > 0 && i % YIELD_CHUNK === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }
        if (uomMissCount > 0) {
          console.warn("[BUG-1566-DIAG] syncProducts: products with no UOM match in uomMap", {
            batchFrom: from - limit,
            uomMissCount,
            batchSize: productsLength,
            samples: uomMissSamples,
          });
        }
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime !== null) {
          const recordsData = await db.products.bulkUpsert(trimProductsBatch(productsData), "value");
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = {
                ...event.type,
                products: { syncStartTime: syncStartTime, syncEndTime, records: event?.products?.records ? event?.products?.records + recordsData : recordsData },
              };
              event.records = (event?.records ? event?.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  products: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type?.products },
                };
              });
          }
        } else {
          const lastId = await db.products.bulkPut(trimProductsBatch(productsData));
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, products: { syncStartTime: syncStartTime, syncEndTime, records: recordsCount } };
              event.records = (event?.records ? event?.records : 0) + products.length;
            });
        }
        trace("ProductsSync", "syncProducts page done", {
          pageCount,
          pageFrom,
          productsLength,
          fetchMs,
          mapWriteMs: Math.round(performance.now() - mapWriteStart),
          totalElapsedMs: Math.round(performance.now() - productsSyncStart),
        });
      } else {
        productsSynced = true;
      }
    }
    trace("ProductsSync", "syncProducts complete", {
      isFullSync: lastUpdatedTime === null,
      pageCount,
      limit,
      recordsCount,
      totalElapsedMs: Math.round(performance.now() - productsSyncStart),
    });
    // Single FTS rebuild at end of full sync (triggers keep FTS in sync per-row;
    // one final rebuild ensures consistency after bulk-replace operations)
    if (lastUpdatedTime === null) {
      try {
        await window.electronAPI.db.rebuildFTS();
      } catch (ftsErr) {
        console.warn("[Sync] FTS rebuild failed — search may be stale:", ftsErr?.message);
      }
    }
  } catch (error) {
    throw error;
  }
};

const syncPricingRules = async (tillData, syncId, tillId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  return new Promise(async (pricingRulesSyncSuccess) => {
    try {
      const paramsInput = buildPricingRulesQuery(tillData.tillAccess.csBunit.cwrPcatalogueId, { lastSyncTime: lastUpdatedTime, tillId, uniqueId: syncId, timeZone });
      const response = await retailInstance({
        data: paramsInput,
      });

      const { getPricingRules } = response.data.data;
      if (response.status === 200 && getPricingRules.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            getPricingRules.map(async (item) => {
              const existingRule = await db.pricingRules.where("mPricingrulesId").equals(item.mPricingrulesId).first();

              if (existingRule) {
                await db.pricingRules.delete(existingRule.id);
                item.syncId = syncId;
                await db.pricingRules.add(item);
                recordsData = recordsData + 1;
              } else {
                item.syncId = syncId;
                recordsData = recordsData + 1;
                await db.pricingRules.add(item);
              }
            })
          );
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, pricingRules: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  pricingRules: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.pricingRules },
                };
              });
          }
        } else {
          await db.pricingRules.clear();
          getPricingRules.forEach((item) => {
            item.syncId = syncId;
          });
          const lastId = await db.pricingRules.bulkPut(getPricingRules);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, pricingRules: { syncStartTime: syncStartTime, syncEndTime, records: getPricingRules.length } };
              event.records = (event.records ? event.records : 0) + getPricingRules.length;
            });
          useConfigStore.getState().loadPricingRules();
          pricingRulesSyncSuccess(lastId);
        }
        useConfigStore.getState().loadPricingRules();
        pricingRulesSyncSuccess();
      } else {
        // BUG-2638: deletePreviousData() no longer empties pricingRules at the start of a Full Sync, so a
        // Full Sync whose answer is "this catalogue has no rules" (200, empty list) clears the old ones here.
        // A failed request throws into the catch below and keeps them.
        if (response.status === 200 && !lastUpdatedTime) {
          await db.pricingRules.clear();
          useConfigStore.getState().loadPricingRules();
        }
        pricingRulesSyncSuccess();
      }
    } catch (error) {
      pricingRulesSyncSuccess();
    }
  });
};

const syncLoyalityData = async (syncId, tillId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  const loyalityQuery = {
    query: `query {
        getLoyaltyconfigurations(lastSyncTime: ${
          lastUpdatedTime ? `"${lastUpdatedTime}"` : null
        }, tillId:"${tillId}",uniqueId: "${syncId}", storeOpsTillId: null, timeZone:"${timeZone}") {
          loyaltylevelId
          name
          accumulationRate
          redemptionRate
          applicableFor
          isDefault
          levelId
          validityPeriod
          minPoints
          maxPoints
          sequence
          upgradeThreshold
          downgrade_threshold
          benefits
          noLoyaltyOnRedemption
          noLoyaltyOnDiscount
          LoyaltyAccumulation {
            loyaltyAccumulationId
            name
            description
            loyaltyLevelId
            pointsPerUnit
            mProductId
            product
            mProductCategoryId
            productCategory
            minPurchase
            startDate
            endDate
            loyaltyEvent
          }
          loyaltyRedemption {
            loyaltyRedemptionId
            ruleName
            description
            loyaltyLevelId
            pointsRequired
            redemptionValue
            minPurchase
            max_redemption
            startDate
            endDate
          }
          loyaltyEventCalenders {
            loyaltyEventCalenderId
            name
            description
            eventType
            startDate
            endDate
          }
        }
      }`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await retailInstance({
        data: loyalityQuery,
      });
      const loyaltyConfigurations = response?.data?.data?.getLoyaltyconfigurations;

      if (loyaltyConfigurations?.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            loyaltyConfigurations.map(async (item) => {
              const existingLoyalty = await db.loyalityData.where("loyaltylevelId").equals(item.loyaltylevelId).toArray();
              if (existingLoyalty.length > 0) {
                item.syncId = syncId;
                recordsData = recordsData + 1;
                await db.loyalityData.update(item.loyaltylevelId, item);
              } else {
                item.syncId = syncId;
                recordsData = recordsData + 1;
                await db.loyalityData.add(item);
              }
            })
          );
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, loyalityData: { syncStartTime: syncStartTime, syncEndTime, records: recordsData } };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  loyalityData: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.loyalityData },
                };
              });
          }
        } else {
          loyaltyConfigurations.forEach((item) => {
            item.syncId = syncId;
          });
          await db.loyalityData.bulkPut(loyaltyConfigurations);
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, loyalityData: { syncStartTime: syncStartTime, syncEndTime, records: loyaltyConfigurations.length } };
              event.records = (event.records ? event.records : 0) + loyaltyConfigurations.length;
            });
        }
      } else {
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

const syncGiftCardData = async (syncId, tillId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  const giftQuery = {
    query: `query {
        getGiftCardType(lastSyncTime: ${lastUpdatedTime ? `"${lastUpdatedTime}"` : null}, tillId:"${tillId}",uniqueId: "${syncId}", storeOpsTillId: null, timeZone:"${timeZone}") {
          cwrGiftcardTypeId
          name
          mProductId
          product
          type
          singleUse
          validity
          balanceLimit
          topupLimit
          prefix
          lengthCardNo
          pinRequired
          initialAmount
          giftCardGroup
          promotionEligible
          variableAmount
          printTemplate {
            cwrPrinttemplateId
            name
            htmlcode
            htmlcode2
            xmlcode
            xmlcode2
            obController
          }
        }
      }`,
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await retailInstance({
        data: giftQuery,
      });

      const giftCardTypes = response.data.data.getGiftCardType;

      if (giftCardTypes.length > 0) {
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        if (lastUpdatedTime) {
          let recordsData = 0;
          await Promise.all(
            giftCardTypes.map(async (item) => {
              const existingGiftCard = await db.giftCardData.where("cwrGiftcardTypeId").equals(item.cwrGiftcardTypeId).toArray();
              if (existingGiftCard.length > 0) {
                recordsData = recordsData + 1;
                item.syncId = syncId;
                await db.giftCardData.update(item.cwrGiftcardTypeId, item);
              } else {
                item.syncId = syncId;
                await db.giftCardData.add(item);
                recordsData = recordsData + 1;
              }
            })
          );

          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = {
                ...event.type,
                giftCardData: { syncStartTime: syncStartTime, syncEndTime, records: recordsData },
              };
              event.records = (event.records ? event.records : 0) + recordsData;
            });
          if (latestIncrementalSync?.length > 0) {
            db.dataSyncSummary
              .where("syncId")
              .equals(latestIncrementalSync[0].syncId)
              .modify((event) => {
                event.type = {
                  ...event.type,
                  giftCardData: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: recordsData, ...event.type.giftCardData },
                };
              });
          }
        } else {
          giftCardTypes.forEach((item) => {
            item.syncId = syncId;
          });
          await db.giftCardData.bulkPut(giftCardTypes);
          safeTrace("giftCardData synced", { records: giftCardTypes.length, incremental: !!lastUpdatedTime, byGroupAndType: summarizeGiftCardTypes(giftCardTypes) });
          db.dataSyncSummary
            .where("syncId")
            .equals(syncId)
            .modify((event) => {
              event.type = { ...event.type, giftCardData: { syncStartTime: syncStartTime, syncEndTime, records: giftCardTypes.length } };
              event.records = (event.records ? event.records : 0) + giftCardTypes.length;
            });
        }
      } else {
        safeTrace("giftCardData sync returned no gift card types", { incremental: !!lastUpdatedTime });
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

const getExpenseCategory = async (syncId) => {
  try {
    const queryPayload = {
      query: `query {
        getExpCategoryDetails(searchKey: null)
      }`,
    };

    const response = await retailInstance({ data: queryPayload });

    const rawData = response.data.data.getExpCategoryDetails;
    const expenseCategory = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    if (expenseCategory?.expenseCategories?.length) {
      await db.getExpenseCategory.clear();
      await db.getExpenseCategory.bulkPut(expenseCategory.expenseCategories);
    }

    return expenseCategory;
  } catch (error) {
    console.error(" Error:", error);
  }
};

const getCompanionRules = async () => {
  await db.getCompanionRules.clear();
  try {
    const queryPayload = {
      query: `query GetCompanionRules { getCompanionRules }`,
    };
    const response = await retailInstance({ data: queryPayload });
    if (response.status === 200) {
      const companionRules = JSON.parse(response?.data?.data?.getCompanionRules) || [];
      await db.getCompanionRules.bulkPut(companionRules);
    }
    return response;
  } catch (error) {
    console.error("Error in getCompanionRules", error);
  }
};

const getRevenueCategories = async (bunitId) => {
  await db.getRevenueCategories.clear();
  try {
    const queryPayload = {
      query: `query GetRevenueCategories($bunitId: String!) { getRevenueCategories(bunitId: $bunitId) }`,
      variables: { bunitId },
    };
    const response = await retailInstance({ data: queryPayload });
    if (response?.status === 200) {
      const rawData = response?.data?.data?.getRevenueCategories;
      const revenueCategories = rawData ? JSON.parse(rawData) : [];
      if (Array.isArray(revenueCategories) && revenueCategories.length > 0) {
        await db.getRevenueCategories.bulkPut(revenueCategories);
        const defaultCategory = revenueCategories.find((c) => c?.isDefault === "Y");
        if (defaultCategory) {
          localStorage.setItem("selectedRevenueCategory", JSON.stringify(defaultCategory));
          useTransactionStore.getState().setSelectedRevenueCategoryId(defaultCategory?.revenueCategoryId || null);
        }
      }
    }
    return response;
  } catch (error) {
    console.error("Error in getRevenueCategories", error);
  }
};

// Fetch a single product from backend on demand when it's missing from the
// local DB. Called from scanner / search / AddProduct paths after a cache
// miss. Upserts into db.products so subsequent lookups hit cache.
//
// Ported from roadmap commit bc3b9831. Kept in syncServices.js so it shares
// the `retailInstance` axios client + auth interceptors.
const syncSingleProductByValue = async (tillData, syncId, tillId, value, { ignoreLastSync = false } = {}) => {
  if (!value) return null;
  const normalisedValue = String(value).trim();
  if (!normalisedValue) return null;

  // For return-flow fallbacks the product was never synced (out-of-stock at sync time)
  // so lastSyncTime would filter it out — pass ignoreLastSync:true to force a full fetch.
  const lastUpdatedTime = ignoreLastSync ? null : localStorage.getItem("lastUpdatedTime");
  try {
    const syncParams = { lastSyncTime: lastUpdatedTime, tillId: tillId || "", uniqueId: syncId || "", timeZone };
    const paramsInput = buildProductsQuery(tillData?.tillAccess?.csBunit?.csBunitId || "", 0, 1, syncParams, { value: normalisedValue });

    const response = await retailInstance({ data: paramsInput });
    const product = response?.data?.data?.getProducts1?.[0];
    if (!product || !product.mProduct) return null;

    let uomData = [];
    try {
      uomData = await db.productUom.where("csUomId").equals(product.mProduct.csUomId).toArray();
    } catch (e) {
      uomData = [];
    }
    if (uomData.length === 0) {
      // [BUG-1566-DIAG] single-product fetch (barcode/search-miss fallback) found no
      // matching row in db.productUom for this csUomId — check whether productUom
      // actually has it (table empty/stale?) or the csUomId itself is the mismatch.
      console.warn("[BUG-1566-DIAG] syncSingleProductByValue: no productUom match", {
        value: normalisedValue,
        csUomId: product.mProduct?.csUomId,
        productUomTableCount: await db.productUom.count(),
      });
    }

    const item = mapProductToDb(product, uomData, { syncId });

    const trimmedItem = trimProductData(item);
    if (!trimmedItem.value) return null;

    const existing = await db.products.where("value").equals(trimmedItem.value).first();
    if (existing) {
      await db.products.where("value").equals(trimmedItem.value).modify(trimmedItem);
    } else {
      await db.products.add(trimmedItem);
    }
    // Single-row writes above bypass the in-memory product search index
    // invalidation (only bulk sync paths mark it dirty). Invalidate explicitly so
    // the freshly fetched product is visible to the very next db.products.search()
    // — covers both the SQLite (Electron) and Dexie (web) adapters. Optional
    // chaining keeps mocked-DB unit tests and any non-index adapter safe.
    db.products.invalidateSearchIndex?.();
    return item;
  } catch (error) {
    console.error("syncSingleProductByValue failed:", error);
    return null;
  }
};

const fetchTablesStatusData = async (tableId) => {
  try {
    const query = `query {
      getTableStatus(tableId: "${tableId}") {
        cSBunitID
        cSClientID
        isactive
        fbTableId
        fbSectionId
        guestName
        guestType
        referredBy
        status
        salesRepId
        guests
        fbtableStatusId
        orderId
        orderStatus
        merge {
          fbMergeId
          fbMergeReason
          fbMTableId
        }
        splitTables {
          isactive
          fbTableId
          fbSectionId
          guestName
          guestType
          referredBy
          status
          salesRepId
          guests
          fbtableStatusId
          splitTable
        }
      }
    }`;

    const response = await retailInstance({
      data: { query },
    });

    return response?.data?.data?.getTableStatus || [];
  } catch (error) {
    console.error(error, "Error in fetch occupied tables");
  }
};

const getFBOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error("Error in getFBOrder", error);
  }
};

const getFBOrderTableData = async (tableId, orderStatus) => {
  try {
    const paramsInput = {
      query: `query {
          getFBOrder(
            tableId: "${tableId}"
            status: "${orderStatus}"
          ) {
            fbOrderId
          cSBunitID
          isactive
          sOrderId
          fbTableId
          orderNo
          fbSectionId
          date
          guests
          guestType
          salesRepId
          guestName
          referredBy
          status
          isCanceled
          discAmount
          fbSplitTable
          merge {
            fbMergeId
            fbMergeReason
            fbTableId
          }
          meta {
            fbOrderMetaId
            key
            value
          }
          lines {
            fbOrderLineId
            line
            mPoductId
            mBatchId
            description
            csUomId
            qty
            unitPrice
            listPrice
            csTaxId
            lineTax
            lineGross
            isOrdered
            sOrderLineId
            isCanceled
            discount
            mPricingRuleId
            name
            value
            grossStd
            grossUnit
            lineStd
            netList
            netStd
            netUnit
            unitTax
            meta {
              fbOrderLineMetaId
              key
              value
            }
          }
          }
        }`,
    };
    const response = await getFBOrder(paramsInput);
    const orders = response?.data?.data?.getFBOrder || [];
    return orders;
  } catch (error) {
    console.error("[SYNC] Error syncing FB orders", error);
  }
};

const syncPricingPolicies = async (tillData, syncId, tillId) => {
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  return new Promise(async (resolve) => {
    try {
      const paramsInput = buildPricingPolicyQuery(tillId);
      const response = await retailInstance({ data: paramsInput });
      const raw = response?.data?.data?.getPricingPolicy;
      if (response?.status === 200 && raw) {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        const rows = mapPricingPolicyToDb(parsed);
        let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        await db.pricingPolicies.clear();
        if (rows.length > 0) {
          await db.pricingPolicies.bulkPut(rows);
        }
        db.dataSyncSummary
          .where("syncId")
          .equals(syncId)
          .modify((event) => {
            event.type = { ...event.type, pricingPolicies: { syncStartTime, syncEndTime, records: rows.length } };
            event.records = (event.records ? event.records : 0) + rows.length;
          });
        useConfigStore.getState().loadPricingPolicies();
      }
      resolve();
    } catch (error) {
      resolve();
    }
  });
};

export {
  syncAllProductCategories,
  syncProductCategories,
  syncProductBrand,
  syncPosSaleTypes,
  syncProductsUom,
  syncProducts,
  syncPricingRules,
  syncLoyalityData,
  syncGiftCardData,
  getExpenseCategory,
  getCompanionRules,
  getRevenueCategories,
  syncSingleProductByValue,
  syncPricingPolicies,
  fetchTablesStatusData,
  getFBOrderTableData,
};
